import React, {Fragment, useState, useEffect} from "react"
import styled from "styled-components"
import moment from "moment"
import {Container, Grid, Row, Col} from "@uprise/grid"
import {TextInputHorizontal} from "@uprise/form"
import {H5, H6} from "@uprise/headings"
import {P} from "@uprise/text"
import {Button} from "@uprise/button"
import {Alert} from "@uprise/alert"
import {useForm, Controller, useFieldArray} from "react-hook-form"
import Toggle from "components/shared/Toggle"
import {backgrounds, extended, primary, semantic} from "@uprise/colors"

import DropDown from "components/shared/DropDown"
import {FixedPricingFeatures} from "helpers/constants"

const PlanContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: ${props => (props.error ? "1px solid #ff9999" : "1px solid #edeafa")};
`

const FormElementContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: ${props => (props.error ? "1px solid #ff9999" : "1px solid #edeafa")};
`

const FormRightItemContainer = styled.div`
	width: 100%;
	display: flex;
	/* justify-content: flex-end; */
	align-items: center;
	border-bottom: ${props => (props.error ? "1px solid #ff9999" : "1px solid #edeafa")};
`

const FormElementLabel = styled(H6)`
	margin: 16px 0 8px 0;

	&:after {
		content: "${props => (props.isRequired ? "*" : "")}";
	}
`

const FormAddOnLabel = styled(H6)`
	margin: 16px 0 8px 0;
	max-width: 170px;
	width: 100%;
	&:after {
		content: "${props => (props.isRequired ? "*" : "")}";
	}
`

const FeaturePricingInputContainer = styled.div`
	align-self: center;
`

const FormInputContainer = styled.div`
	flex-basis: 60%;
	align-self: center;
`

const FormPrePuchasedInputContainer = styled.div`
	align-self: center;
	max-width: 80px;
	width: 100%;
`

const FormTextInput = styled(TextInputHorizontal)`
	border-radius: 4px;
	max-width: 80px;
	border: solid 1px #d4cbfb;
`

const FormNumericInput = styled(TextInputHorizontal)`
	border-radius: 4px;
	border: solid 1px #d4cbfb;
`

const FormTextArea = styled.textarea`
	border: none;
	resize: none;
	width: 100%;
`

const ButtonContainer = styled.div`
	display: flex;
`

const PrimaryButton = styled(Button)`
	width: 100px;
	height: 50px;
	margin: 0 6px;
`

const SecondaryButton = styled(Button)`
	width: 100px;
	height: 50px;
	margin: 0 6px;
	border-color: transparent;
	background-color: #f6f4fc;
`

const FormSpanText = styled(H6)`
	margin: 0 0 0 12px;
`

const FormRightContainer = styled.div`
	display: flex;
	flex-basis: 60%;
	justify-content: space-between;
`

const MessageStyles = styled.label`
	font-size: 12px;
	color: ${props => {
		if (props.focused) {
			return `${extended.blue.one}`
		} else if (props.validation) {
			return `${semantic.error}`
		} else {
			return `${extended.purple.five}`
		}
	}};
	display: block;
	margin-top: 8px;
	transition: font-size 0.2s;
`

const Plan = ({onComplete, onPrevious}) => {
	const [isFeaturedIncluded, setFeaturesIncluded] = useState(false)

	const {register, control, handleSubmit, watch, reset, trigger, setError, errors} = useForm({
		// defaultValues: {}; you can populate the fields by this attribute
		shouldUnregister: false
	})

	const {fields, append, prepend, remove, swap, move, insert} = useFieldArray({
		control,
		name: "featurePricing"
	})

	const watchFeaturePricing = fields.length > 0 ? watch("featurePricing") : []
	const showPricingType = watch("pricingType")
	useEffect(() => {
		if (showPricingType === "variable") {
			remove()
			FixedPricingFeatures.map((item, index) => {
				append(item)
				return null
			})
		} else if (showPricingType === "fixed") {
			remove()
			FixedPricingFeatures.map((item, index) => {
				append(item)
				return null
			})
		}
	}, [showPricingType])

	return (
		<div>
			<form onSubmit={handleSubmit(data => onComplete(data))}>
				<Row>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<H5>Plan</H5>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<H5>Add-on</H5>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<PlanContainer>
							<FormElementLabel style={{width: "100%"}} isRequired={true}>
								Pricing Type
							</FormElementLabel>
							<FormInputContainer style={{width: "100%"}}>
								<Controller
									name={"pricingType"}
									control={control}
									defaultValue={""}
									rules={{required: "Pricing Type is Required"}}
									render={props => (
										<DropDown
											variant='primary'
											testId='pricingType'
											items={[
												{
													label: "Variable Fee",
													value: "variable"
												},
												{
													label: "Fixed Annual Fee",
													value: "fixed"
												}
											]}
											label='Pricing Type'
											onChange={item => props.onChange(item.value)}
											initialSelectedItem={""}
											menuStyle={{position: "absolute", zIndex: 2}}
										/>
									)}
								/>
							</FormInputContainer>
						</PlanContainer>
						{errors["pricingType"] && errors["pricingType"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["pricingType"]}>
								{errors["pricingType"] && errors["pricingType"].message}
							</MessageStyles>
						) : null}
						{showPricingType && showPricingType !== "" ? (
							<Fragment>
								<PlanContainer>
									<FormElementLabel style={{width: "100%"}}>Cost/Licence</FormElementLabel>
									<FormInputContainer
										style={{
											marginLeft: "10px",
											marginRight: "10px"
										}}>
										<Controller
											control={control}
											name={"price"}
											defaultValue={0}
											rules={{
												min: {
													value: 0,
													message: "Cost cannot be negative"
												}
											}}
											as={<FormTextInput id='price' type='number' placeholder='' />}
										/>
									</FormInputContainer>
								</PlanContainer>
								{errors["price"] && errors["price"].message ? (
									<MessageStyles htmlFor={1} focused={false} validation={errors["price"]}>
										{errors["price"] && errors["price"].message}
									</MessageStyles>
								) : null}

								<FormRightItemContainer>
									<FormElementLabel style={{width: "100%"}}>No. of licenses</FormElementLabel>
									<FormInputContainer
										style={{
											marginLeft: "10px",
											marginRight: "10px"
										}}>
										<Controller
											control={control}
											name={"licenses"}
											rules={{
												min: {
													value: 0,
													message: "Amount cannot be negative"
												}
											}}
											defaultValue={0}
											as={<FormTextInput id='licenses' type='number' placeholder='' />}
										/>
									</FormInputContainer>
								</FormRightItemContainer>
								{errors["licenses"] && errors["licenses"].message ? (
									<MessageStyles htmlFor={1} focused={false} validation={errors["licenses"]}>
										{errors["licenses"] && errors["licenses"].message}
									</MessageStyles>
								) : null}
								{fields.map((el, index) => {
									return (
										<Fragment key={index}>
											<PlanContainer>
												<div style={{maxWidth: "170px", minWidth: "170px"}}>
													<FormElementLabel>{el.label}</FormElementLabel>
												</div>
												<FeaturePricingInputContainer style={{margin: "0 12px 0 6px"}}>
													<Controller
														name={`featurePricing[${index}].enabled`}
														control={control}
														defaultValue={true}
														render={props => (
															<Toggle
																checked={props.value}
																onChange={() => {
																	props.onChange(!props.value)
																	console.log(
																		"testing showPricing type",
																		showPricingType
																	)
																}}
																backgroundColorChecked={"#7d60ff"}
																width='42'
																height='24'
																sliderHeight='20.8'
																sliderWidth='20.8'
																translate='18'
															/>
														)}
													/>
												</FeaturePricingInputContainer>
												{watchFeaturePricing[index].id !== "care-navigator" && (
													<div>
														<FormElementLabel>{`${
															watchFeaturePricing[index].enabled ? "Included" : "Excluded"
														}`}</FormElementLabel>
													</div>
												)}
												{watchFeaturePricing[index].enabled &&
												watchFeaturePricing[index].id !== "care-navigator" &&
												showPricingType === "variable" ? (
													<Fragment>
														<FormElementLabel
															style={{marginLeft: "17px", marginRight: "12px"}}>
															Cost
														</FormElementLabel>
														<FormInputContainer
															style={{
																maxWidth: "56px",
																marginLeft: "12px",
																marginRight: "12px"
															}}>
															<Controller
																control={control}
																name={`featurePricing[${index}].fee`}
																rules={{
																	min: {
																		value: 0,
																		message: "Cost cannot be negative"
																	}
																}}
																defaultValue={0}
																as={
																	<FormTextInput
																		type='number'
																		placeholder=''
																		minLength={0}
																	/>
																}
															/>
														</FormInputContainer>
														<FormElementLabel
															style={{marginLeft: "12px", marginRight: "12px"}}>
															per
														</FormElementLabel>
														<FeaturePricingInputContainer>
															<Controller
																name={`featurePricing[${index}].per`}
																control={control}
																defaultValue={"hour"}
																render={props => (
																	<DropDown
																		isFirst={true}
																		variant='primary'
																		items={[
																			{
																				label: "Hour",
																				value: "hour"
																			},
																			{
																				label: "Per Fleet",
																				value: "fleet"
																			}
																		]}
																		label='Session'
																		onChange={item => props.onChange(item.value)}
																		initialSelectedItem={""}
																		menuStyle={{position: "absolute", zIndex: 2}}
																	/>
																)}
															/>
														</FeaturePricingInputContainer>
													</Fragment>
												) : null}
											</PlanContainer>
											{errors[`featurePricing`]?.[index]?.fee &&
											errors[`featurePricing`]?.[index]?.fee?.message ? (
												<MessageStyles
													htmlFor={1}
													focused={false}
													validation={errors[`featurePricing`]?.[index]?.fee}>
													{errors[`featurePricing`]?.[index]?.fee &&
														errors[`featurePricing`]?.[index]?.fee?.message}
												</MessageStyles>
											) : null}
										</Fragment>
									)
								})}
							</Fragment>
						) : null}
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<PlanContainer>
							<FormAddOnLabel isRequired={showPricingType === "variable" ? true : false}>
								Add-on type
							</FormAddOnLabel>
							<FormInputContainer>
								<Controller
									name={"addOnType"}
									defaultValue={""}
									rules={{
										required: showPricingType === "variable" ? "Add On Type is Required" : false
									}}
									control={control}
									render={props => (
										<DropDown
											variant='primary'
											testId='addOnType'
											items={[
												{
													label: "Pre-Purchased Hours",
													value: "pre-purchased"
												}
											]}
											label='Select Add-on type'
											onChange={item => props.onChange(item.value)}
											initialSelectedItem={""}
											menuStyle={{position: "absolute", zIndex: 2}}
										/>
									)}
								/>
							</FormInputContainer>
						</PlanContainer>

						{errors["addOnType"] && errors["addOnType"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["addOnType"]}>
								{errors["addOnType"] && errors["addOnType"].message}
							</MessageStyles>
						) : null}

						<PlanContainer>
							<FormAddOnLabel isRequired={false}>Pre-purchased</FormAddOnLabel>

							<FormRightContainer>
								<FormPrePuchasedInputContainer>
									<Controller
										control={control}
										name={"prePurchased"}
										defaultValue={""}
										rules={{
											min: {
												value: 0,
												message: "Hours cannot be negative"
											}
										}}
										as={<FormTextInput id='prePurchased' type='number' placeholder='' />}
									/>
								</FormPrePuchasedInputContainer>
								<FormSpanText color={"#6d6c79"}>hours</FormSpanText>
							</FormRightContainer>
						</PlanContainer>
						{errors["prePurchased"] && errors["prePurchased"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["prePurchased"]}>
								{errors["prePurchased"] && errors["prePurchased"].message}
							</MessageStyles>
						) : null}

						<PlanContainer>
							<FormAddOnLabel isRequired={false}>Price</FormAddOnLabel>
							<FormRightContainer>
								<FormPrePuchasedInputContainer>
									<Controller
										control={control}
										name={"prePurchasedPrice"}
										defaultValue={""}
										rules={{
											min: {
												value: 0,
												message: "Price cannot be negative"
											}
										}}
										as={<FormTextInput id='prePurchasedPrice' type='number' placeholder='' />}
									/>
								</FormPrePuchasedInputContainer>
								<FormSpanText color={"#6d6c79"}>Per hour</FormSpanText>
							</FormRightContainer>
						</PlanContainer>
						{errors["prePurchasedPrice"] && errors["prePurchasedPrice"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["prePurchasedPrice"]}>
								{errors["prePurchasedPrice"] && errors["prePurchasedPrice"].message}
							</MessageStyles>
						) : null}
					</Col>
				</Row>

				<Row className='flex-row-reverse mt-4'>
					<ButtonContainer>
						<PrimaryButton type='submit' variant={"primary"} title={"Done"} data-testid='doneButton' />
					</ButtonContainer>
					<ButtonContainer>
						<SecondaryButton
							type='button'
							variant={"secondary"}
							title={"Previous"}
							onClick={onPrevious}
							data-testid='previousButton'
						/>
					</ButtonContainer>
				</Row>
			</form>
		</div>
	)
}

export default Plan
